import { useRef, useLayoutEffect, useCallback, useState, useEffect } from 'react';
import { Button, Card, Table, Space, Tooltip, theme } from 'antd';
import OperationLogFilterForm from './OperationLogFilterForm';
import OperationLogInfo from './OperationLogInfo';
import { fullPagerConfig } from '@/utils/property';
import { get } from '@/core/request';
import './OperationList.less';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
export default function OperationLogList() {
    const { token } = theme.useToken();
    const navigate = useNavigate();

    const [tableData, setTableData] = useState({});
    const [filterObj, setFilterObj] = useState({});

    const handleFilterFinish = value => {
        setFilterObj(value);
    };

    const [updatedByStaffId, setUpdatedByStaffId] = useState([]);

    useEffect(() => {
        get('/enterprise/staffs/get_all').then(res => {
            setUpdatedByStaffId(res.data);
        });
    }, []);

    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);

    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const { params, phone, beginTime, endTime } = filterObj;

    const getData = useCallback(() => {
        get(`/enterprise/logs/query_page`, {
            limit: pageSize,
            page: currPage,
            params,
            phone,
            beginTime,
            endTime,
        }).then(res => {
            if (res.status_code === 200) {
                const { list } = res.data;
                list.forEach(item => {
                    item.key = item.id;
                });
                setTableData(res.data);
            }
        });
    }, [pageSize, currPage, params, phone, beginTime, endTime]);

    useEffect(() => {
        getData();
    }, [getData]);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    const drawer = useRef();
    const handleClickInfo = useCallback(row => {
        return () => {
            drawer.current.show(row);
        };
    }, []);

    const handleRefresh = useCallback(() => {
        getData();
    }, [getData]);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const columns = [
        { title: '#', dataIndex: 'id' },
        { title: '请求路由', dataIndex: 'route' },
        { title: '请求方法', dataIndex: 'requestMethod' },
        { title: '操作用户', dataIndex: 'staffName' },
        { title: '操作时间', dataIndex: 'createdAt' },
        {
            title: '操作',
            dataIndex: 'operate',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <Space className="operation-btns">
                    <Button size="small" type="link" onClick={handleClickInfo(row)}>
                        查看详情
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container list-page-container enterprise-oplog-list-page">
            <PageHeader title="后台操作日志" breadcrumb={[{ label: '企业管理', path: '/enterprise' }, { label: '后台操作日志' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="enterprise-oplog-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <OperationLogFilterForm handleFilterFinish={handleFilterFinish} updatedByStaffId={updatedByStaffId} />
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container enterprise-oplog-table-region">
                <div className="main-toolbar enterprise-oplog-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button onClick={() => navigate('/enterprise/operation/stat')}>后台操作统计</Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap enterprise-oplog-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData.list}
                        loading={false}
                        bordered
                        sticky
                        size="medium"
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={paginationProps}
                    />
                </div>
            </div>

            <OperationLogInfo ref={drawer}></OperationLogInfo>
        </div>
    );
}
